/**
 * Analysis API Service
 *
 * Communicates with the Protocol Analysis Lambda for:
 * - Analysis generation (triggering)
 * - Status checking with worker progress
 * - Index and chunk retrieval
 */

import axios, { AxiosInstance } from 'axios';
import { config } from '../config';
import { AnalysisTriggerResponse, AnalysisStatusResponse, AnalysisStatusType } from '../types';

class AnalysisService {
  private client: AxiosInstance;
  private accessToken: string | null = null;

  /** Whether the analysis API URL is configured */
  get isConfigured(): boolean {
    return !!config.analysisApiUrl;
  }

  constructor() {
    this.client = axios.create({
      baseURL: config.analysisApiUrl || 'http://analysis-not-configured',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      timeout: 30000,
    });

    this.client.interceptors.request.use(
      (reqConfig) => {
        if (this.accessToken) {
          reqConfig.headers['Authorization'] = `Bearer ${this.accessToken}`;
        }
        return reqConfig;
      },
      (error) => Promise.reject(error)
    );
  }

  setAccessToken(token: string | null) {
    this.accessToken = token;
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  /** Parse Lambda response body (handles double-encoded JSON) */
  private parseBody(data: any): any {
    if (data?.body) {
      return typeof data.body === 'string' ? JSON.parse(data.body) : data.body;
    }
    return data;
  }

  /**
   * Normalize analysis status string to uppercase enum value.
   * The API returns lowercase ("completed"), but our types use uppercase ("COMPLETED").
   * Mirrors Flutter's _parseStatus() which calls toUpperCase() before matching.
   */
  private normalizeStatus(status: string | undefined): AnalysisStatusType {
    switch (status?.toUpperCase()) {
      case 'QUEUED': return 'QUEUED';
      case 'RUNNING': return 'RUNNING';
      case 'COMPLETED': return 'COMPLETED';
      case 'FAILED': return 'FAILED';
      case 'NOT_FOUND': return 'NOT_FOUND';
      default: return 'NOT_FOUND';
    }
  }

  // ============================================
  // ANALYSIS GENERATION
  // ============================================

  /**
   * Trigger analysis generation for a protocol
   *
   * Returns null if an analysis is already running (409 Conflict),
   * or if the analysis API is not configured.
   */
  async triggerAnalysis(
    protocolId: string,
    platform: string,
    force: boolean = false
  ): Promise<AnalysisTriggerResponse | null> {
    if (!this.isConfigured) {
      return null;
    }
    try {
      const params: Record<string, string> = { platform };
      if (force) params.force = 'true';
      const response = await this.client.post(`/analysis/${protocolId}/trigger`, null, { params });
      return this.parseBody(response.data);
    } catch (error: any) {
      if (error?.response?.status === 409) {
        return null; // Already running
      }
      throw error;
    }
  }

  // ============================================
  // STATUS CHECKING
  // ============================================

  /**
   * Get current analysis status
   *
   * Returns detailed status including worker progress and LLM stats.
   * Returns NOT_FOUND if the analysis API is not configured.
   */
  async getStatus(
    protocolId: string,
    platform: string
  ): Promise<AnalysisStatusResponse> {
    if (!this.isConfigured) {
      return { status: 'NOT_FOUND', protocol_id: protocolId, platform };
    }
    try {
      const response = await this.client.get(`/analysis/${protocolId}/status`, {
        params: { platform },
      });
      const body = this.parseBody(response.data);
      // Normalize status to uppercase (API returns lowercase "completed", we need "COMPLETED")
      return { ...body, status: this.normalizeStatus(body.status) };
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return {
          status: 'NOT_FOUND',
          protocol_id: protocolId,
          platform,
        };
      }
      throw error;
    }
  }

  // ============================================
  // INDEX & CHUNKS
  // ============================================

  /**
   * Get the master index with all chunks and semantic keys
   *
   * Returns null if analysis is not completed yet or API not configured.
   */
  async getIndex(protocolId: string, platform: string): Promise<any | null> {
    if (!this.isConfigured) return null;
    try {
      const response = await this.client.get(`/analysis/${protocolId}/index`, {
        params: { platform },
      });
      return this.parseBody(response.data);
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Get a specific chunk's content
   *
   * chunkId examples: 'protocol-conditions', 'F40015-actions', 'F40015-page-U3'
   */
  async getChunk(
    protocolId: string,
    platform: string,
    chunkId: string
  ): Promise<any | null> {
    if (!this.isConfigured) return null;
    try {
      const response = await this.client.get(`/analysis/${protocolId}/chunk/${chunkId}`, {
        params: { platform },
      });
      return this.parseBody(response.data);
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  // ============================================
  // HEALTH CHECK
  // ============================================

  async healthCheck(): Promise<boolean> {
    try {
      await this.client.get('/');
      return true;
    } catch {
      return false;
    }
  }
}

// Export singleton instance
export const analysisService = new AnalysisService();
